import authAxiosService from '@/api/authAxios.service';
import { jwtDecode } from 'jwt-decode';
import {DEFAULT_THEME, isValidTheme, isValidColorVisionType } from '@/config/themes';

const login = {
  namespaced: true,
  state: () => ({
    // Auth flow
    loading: false,
    authCode: null,
    accessToken: null,

    // User profile
    firstName: null,
    lastName: null,
    employeeID: null,
    emailID: null,
    portalID: null,
    portalOwner: false,
    theme: null,
    colorVisionType: null,
    userName: null,
    userPerms: [],
    timeFormat: null,
    showGridLines: null,
    groupByLoadStatus: false,
    combinePickupDropDateTime: false,
    useShippingTimeOnPickDropSelect: false,

    // Please get these out of here
    isSidebarVisible: false,
    isShowCreateLoad: false,
    sourceLoadId: "",
    isLoadRefresh: false,
    cities: [],
    states: [],
    isShowNewDriver: false,
  }),

  getters: {
    isLoading(state) {
      return state.loading;
    },
    isAuthenticated(state) {
      if (state.accessToken) {
        let decodedToken = jwtDecode(state.accessToken);
        return decodedToken.exp > Date.now() / 1000;
      }
      return false;
    },
    isTokenExpired(state) {
      if (state.accessToken) {
        let decodedToken = jwtDecode(state.accessToken);
        return decodedToken.exp <= Date.now() / 1000;
      }
      return true;
    },
    userPerms(state) {
      return state.userPerms || [];
    },
    userHasPerm: (state) => (permission) => {
      return (state.userPerms || []).includes(permission);
    },
    fullName(state) {
      if (state.firstName && state.lastName) {
        return `${state.firstName} ${state.lastName}`;
      }
      return '';
    },
  },

  mutations: {
    setTheme(state, payload) {
      state.theme = isValidTheme(payload.theme) ? payload.theme : DEFAULT_THEME;
      state.colorVisionType = isValidColorVisionType(payload.colorVisionType) ? payload.colorVisionType : null;
    },
    setTime(state, payload) {
      state.timeFormat = payload.timeFormat;
    },
    setShowGridLines(state, payload) {
      state.showGridLines = payload.showGridLines;
    },
    setGroupByLoadStatus(state, payload) {
      state.groupByLoadStatus = payload.groupByLoadStatus;
    },
    setCombinePickupDropDateTime(state, payload) {
      state.combinePickupDropDateTime = payload.combinePickupDropDateTime;
    },
    setUseShippingTimeOnPickDropSelect(state, payload) {
      state.useShippingTimeOnPickDropSelect = payload.useShippingTimeOnPickDropSelect;
    },
    toggleSidebar(state, payload) {
      state.isSidebarVisible = payload.isSidebarVisible;
    },
    closeSidebar(state, payload) {
      state.isSidebarVisible = payload.isSidebarVisible;
    },
    openCreateLoad(state) {
      state.isShowCreateLoad = true;
    },
    closeCreateLoad(state, payload) {
      state.isShowCreateLoad = false;
      state.isLoadRefresh = payload;
    },
    openNewDriver(state) {
      state.isShowNewDriver = true;
    },
    closeNewDriver(state){
      state.isShowNewDriver = false;
    },
    openSourceLoadPopup(state, payload) {
      state.sourceLoadId = payload;
    },
    setCities(state, payload) {
      state.cities = payload;
    },
    setStates(state, payload) {
      state.states = payload;
    },
    setLoading(state, payload) {
      state.loading = payload;
    },
    setAuthCode(state, payload) {
      state.authCode = payload;
    },
    setAccessToken(state, payload) {
      state.accessToken = payload;
    },
    setUserPerms(state, payload) {
      state.userPerms = payload;
    },
    applyLoginData(state, authData) {
      state.accessToken = authData.token;
      state.userPerms = jwtDecode(authData.token)?.userPerms || [];
      if (authData.profile) {
        state.firstName = authData.profile.firstName;
        state.lastName = authData.profile.lastName;
        state.portalID = authData.profile.portalID;
        state.employeeID = authData.profile.employeeID;
        state.emailID = authData.profile.emailID;
        state.portalOwner = authData.profile.portalOwner;
        state.userName = authData.profile.userName;
      }

      const setting = authData.employeeSettings?.find(s => s.KeyCode === "UITheme");
      const uiSettings = JSON.parse(setting?.SettingValue?.startsWith("{") ? setting.SettingValue : `{ "theme": "${setting?.SettingValue}" }`);

      state.theme = uiSettings.theme;
      state.colorVisionType = uiSettings.colorVisionType ?? "default";

      state.timeFormat = authData.employeeSettings?.find(setting => setting.KeyCode === "TimeFormat")?.SettingValue;
      state.showGridLines = authData.employeeSettings?.find(setting => setting.KeyCode === "IsLoadGridLines")?.SettingValue;
      state.groupByLoadStatus = authData.employeeSettings?.find(setting => setting.KeyCode === "GroupByLoadStatus")?.SettingValue;
      state.combinePickupDropDateTime = authData.employeeSettings?.find(setting => setting.KeyCode === "CombinePickupDropDateTimeOnMyLoads")?.SettingValue;
      state.useShippingTimeOnPickDropSelect = authData.employeeSettings?.find(setting => setting.KeyCode === "UseShippingTimeOnPickDropSelect")?.SettingValue;
    },
    clearAuthData(state) {
      state.authCode = null;
      state.accessToken = null;
      state.firstName = null;
      state.lastName = null;
      state.portalID = null;
      state.employeeID = null;
      state.emailID = null;
      state.portalOwner = false;
      state.userName = null;
      state.userPerms = [];
    },
  },

  actions: {
    updateTheme({ commit },  { theme, colorVisionType }) {
      commit("setTheme", { theme, colorVisionType });
    },
    updateTime({ commit }, time) {
      let payload = {
        timeFormat: time,
      };
      commit("setTime", payload);
    },
    updateShowGridLines({ commit }, showGridLines) {
      let payload = {
        showGridLines: showGridLines,
      };
      commit("setShowGridLines", payload);
    },
    updateGroupByLoadStatus({ commit }, groupByLoadStatus) {
      let payload = {
        groupByLoadStatus: groupByLoadStatus,
      };
      commit("setGroupByLoadStatus", payload);
    },
    updateCombinePickupDropDateTime({ commit }, combinePickupDropDateTime) {
      let payload = {
        combinePickupDropDateTime: combinePickupDropDateTime,
      };
      commit("setCombinePickupDropDateTime", payload);
    },
    updateUseShippingTimeOnPickDropSelect({ commit }, useShippingTimeOnPickDropSelect) {
      let payload = {
        useShippingTimeOnPickDropSelect: useShippingTimeOnPickDropSelect,
      };
      commit("setUseShippingTimeOnPickDropSelect", payload);
    },
    toggleSidebar({ commit }, isSidebarVisible) {
      let payload = {
        isSidebarVisible: isSidebarVisible,
      };
      commit("toggleSidebar", payload);
    },
    closeSidebar({ commit }, isSidebarVisible) {
      let payload = {
        isSidebarVisible: isSidebarVisible,
      };
      commit("closeSidebar", payload);
    },
    setAuthorizationCode({ commit }, code) {
      commit('setAuthCode', code);
    },
    async getAuthorizationCode() {
      let url = await authAxiosService.getAuthorizationCodeURL();
      window.open(url, "_self");
    },
    async tryLDILogin({ commit, state, dispatch }, silent = false) {
      // Prevent concurrent login attempts
      if (state.loading) {
        return false;
      }
      
      commit('setLoading', true);
      let success = false;
      
      // First try using the auth code
      if (state.authCode) {
        let authResp = await authAxiosService.exchangeAuthCodeForTokens(state.authCode);
        if (authResp) {
          commit('applyLoginData', authResp);
          commit('setAuthCode', null);
          success = true;
        }
      }
      // If not then try to use the refresh token
      else {
        try {
          await authAxiosService.useRefreshToken();
          success = true;
        } catch (error) {
          // If all else fails and not silent, get a new auth code
          if (!silent) {
            dispatch('getAuthorizationCode');
          }
        }
      }
      
      commit('setLoading', false);
      return success;
    },
    async logout({ commit }) {
      await authAxiosService.logout();
      commit('clearAuthData');
    },
  },
};

export default login;
